/* eslint-disable react/prop-types */

import React, { useEffect, useState, useRef } from "react";
import * as echarts from "echarts";
import { merge } from "lodash";

import styled from "styled-components";
import { useThemeSwitchingContext } from "src/contexts/ThemeSwitchingContext";

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
`;

export const AIChart = ({ spec }) => {
  const { isDarkTheme, theme } = useThemeSwitchingContext();
  const chartTextColor = theme.colors.chartAxisLabel;
  const chartTooltipBg = theme.colors.chartTooltipBackground;
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  // Cache key includes isDarkTheme so the chart re-renders on theme switch.
  const optionsCacheKey = useRef("{}:light");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let runtime;
    let resizeObserver;
    try {
      // Initialize chart only once
      runtime = echarts.init(containerRef.current);
      viewRef.current = runtime;

      // ResizeObserver watches the container itself for any size changes
      resizeObserver = new ResizeObserver(() => {
        if (viewRef.current) {
          viewRef.current.resize();
        }
      });
      resizeObserver.observe(containerRef.current);
    } catch (err) {
      console.error("Error initializing chart:", err);
      setError(`Error initializing chart: ${err.message}`);
    }

    return () => {
      resizeObserver?.disconnect();
      if (viewRef.current) {
        viewRef.current.dispose();
        viewRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Update chart data when spec or theme changes.
  useEffect(() => {
    const cacheKey = `${JSON.stringify(spec)}:${isDarkTheme ? "dark" : "light"}`;
    if (viewRef.current && spec && cacheKey !== optionsCacheKey.current) {
      try {
        // Fix sankey self-links before applying options.
        const safeSeries = spec.series?.map((s) =>
          s.type === "sankey" && s.links
            ? { ...s, links: s.links.filter((l) => l.source !== l.target) }
            : s
        );
        const safeSpec = safeSeries ? { ...spec, series: safeSeries } : spec;
        const mergedOptions = merge({}, safeSpec, {
          textStyle: { color: chartTextColor },
          title: { textStyle: { color: chartTextColor } },
          legend: { textStyle: { color: chartTextColor } },
          xAxis: {
            nameTextStyle: { color: chartTextColor },
            axisLabel: { color: chartTextColor },
          },
          yAxis: {
            nameTextStyle: { color: chartTextColor },
            axisLabel: { color: chartTextColor },
          },
          grid: {
            containLabel: true,
            outerBoundsContain: "all",
            outerBoundsMode: "same",
            xAxisName: { show: false },
            yAxisName: { show: false },
          },
          tooltip: {
            confine: true,
            backgroundColor: chartTooltipBg,
            textStyle: { color: chartTextColor },
          },
          toolbox: {
            show: true,
            feature: {
              // Restrict box-zoom to the x-axis. A value-axis dataZoom defaults
              // to filterMode "filter", which drops points and blanks the
              // areaStyle fill of stacked series (e.g. floating confidence/gap
              // bands render with an empty path), so scope the y-axis out.
              dataZoom: { show: true, yAxisIndex: false },
              saveAsImage: { show: true, name: "chart" },
            },
          },
        });
        viewRef.current.setOption(mergedOptions, { notMerge: true });
        setError(null);
        optionsCacheKey.current = cacheKey;
      } catch (err) {
        console.error("Error updating chart:", err);
        setError(`Error updating chart: ${err.message}`);
      }
    }
  }, [spec, chartTextColor, chartTooltipBg, isDarkTheme]);

  return (
    <ChartContainer>
      {error && <div>{error}</div>}
      <div style={{ width: "100%", height: "300px" }} ref={containerRef} />
    </ChartContainer>
  );
};
